import { Icons } from "@/components/ui/icons";
import {
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
} from "@/components/ui/context-menu";
import { ColorSubmenuItem, RenameSubmenuItem } from "./browse-tree-submenus";
import { useT } from "@/i18n";
import { panelsHeaderMessages } from "@/i18n/messages/panels-header";

interface ProjectContextMenuProject {
  id: string;
  name: string;
  color: string;
  isHome?: boolean;
  isPinned?: boolean;
}

interface ProjectContextMenuContentProps {
  project: ProjectContextMenuProject;
  spaces?: Array<{ id: string; label: string; active?: boolean }>;
  onRename?: (newName: string) => void;
  onSetColor?: (projectId: string, color: string) => void;
  onPin?: (projectId: string) => void;
  onSettings?: (projectId: string) => void;
  onSelectSpace?: (projectId: string, spaceId: string) => void;
  onNewSpace?: (projectId: string) => void;
  onDelete?: (projectId: string) => void;
}

export function ProjectContextMenuContent({
  project,
  onRename,
  onSetColor,
  onPin,
  onSettings,
  onDelete,
}: ProjectContextMenuContentProps) {
  const t = useT(panelsHeaderMessages);
  return (
    <ContextMenuContent>
      <RenameSubmenuItem
        currentName={project.name}
        onSubmit={(name) => onRename?.(name)}
        data-testid={`browse-tree-project-${project.id}-rename`}
      />
      {!project.isHome && (
        <ColorSubmenuItem
          currentColor={project.color}
          onSelect={(color) => onSetColor?.(project.id, color)}
          data-testid={`browse-tree-project-${project.id}-set-color`}
        />
      )}
      <ContextMenuItem
        icon={<Icons.Pin />}
        onClick={() => onPin?.(project.id)}
        data-testid={`browse-tree-project-${project.id}-pin`}
      >
        {project.isPinned ? t("unpin") : t("pinToTop")}
      </ContextMenuItem>
      <ContextMenuItem
        icon={<Icons.Settings />}
        onClick={() => onSettings?.(project.id)}
        data-testid={`browse-tree-project-${project.id}-settings`}
      >
        {t("projectSettings")}
      </ContextMenuItem>
      {!project.isHome && (
        <>
          <ContextMenuSeparator />
          <ContextMenuItem
            destructive
            icon={<Icons.Trash2 />}
            onClick={() => onDelete?.(project.id)}
            data-testid={`browse-tree-project-${project.id}-delete`}
          >
            {t("delete")}
          </ContextMenuItem>
        </>
      )}
    </ContextMenuContent>
  );
}

export type { ProjectContextMenuProject };
